class BCProductGallery {
  constructor(section) {
    this.section = section;
    this.mainImage = section.querySelector('[data-bc-main-image]');
    this.mainMedia = section.querySelector('.bc-product__main-media');
    this.thumbs = Array.from(section.querySelectorAll('[data-bc-thumb]'));
    this.dots = Array.from(section.querySelectorAll('[data-bc-dot]'));
    this.prevButtons = Array.from(section.querySelectorAll('[data-bc-prev]'));
    this.nextButtons = Array.from(section.querySelectorAll('[data-bc-next]'));
    this.modal = section.querySelector('[data-bc-modal]');
    this.modalImage = section.querySelector('[data-bc-modal-image]');
    this.modalClose = section.querySelector('[data-bc-modal-close]');
    this.openButtons = Array.from(section.querySelectorAll('[data-bc-open-modal]'));
    this.current = 0;
    this.items = this.thumbs.map((thumb) => ({
      mediaId: thumb.dataset.mediaId,
      full: thumb.dataset.fullSrc,
      preview: thumb.dataset.previewSrc,
      alt: thumb.dataset.alt || '',
    }));

    if (!this.mainImage || this.items.length === 0) return;

    this.bind();
    this.update();
  }

  bind() {
    this.thumbs.forEach((thumb, index) => {
      thumb.addEventListener('click', () => this.goTo(index));
    });

    this.dots.forEach((dot, index) => {
      dot.addEventListener('click', () => this.goTo(index));
    });

    this.prevButtons.forEach((button) => {
      button.addEventListener('click', () => this.goTo(this.current - 1));
    });

    this.nextButtons.forEach((button) => {
      button.addEventListener('click', () => this.goTo(this.current + 1));
    });

    this._bindSwipe();

    this.openButtons.forEach((button) => {
      button.addEventListener('click', () => this.openModal());
    });

    this.modalClose?.addEventListener('click', () => this.closeModal());
    this.modal?.addEventListener('click', (event) => {
      if (event.target === this.modal) this.closeModal();
    });

    this._onKeyDown = (event) => {
      if (event.key === 'Escape') this.closeModal();
      if (event.key === 'ArrowLeft') this.goTo(this.current - 1);
      if (event.key === 'ArrowRight') this.goTo(this.current + 1);
    };
  }

  goTo(index) {
    const total = this.items.length;
    this.current = (index + total) % total;
    this.update();
  }

  /** Horizontal touch swipe on the main photo — mobile replacement for the arrows/thumbs. */
  _bindSwipe() {
    if (!this.mainMedia) return;

    let startX = 0;
    let startY = 0;

    this.mainMedia.addEventListener('touchstart', (event) => {
      const touch = event.changedTouches[0];
      startX = touch.clientX;
      startY = touch.clientY;
    }, { passive: true });

    this.mainMedia.addEventListener('touchend', (event) => {
      const touch = event.changedTouches[0];
      const deltaX = touch.clientX - startX;
      const deltaY = touch.clientY - startY;

      if (Math.abs(deltaX) < 40 || Math.abs(deltaX) < Math.abs(deltaY)) return;
      this.goTo(this.current + (deltaX < 0 ? 1 : -1));
    }, { passive: true });
  }

  /** Switches to the variant's own image, if it has one and it's in this gallery. */
  goToMedia(mediaId) {
    if (!mediaId) return;
    const index = this.items.findIndex((item) => item.mediaId === String(mediaId));
    if (index !== -1) this.goTo(index);
  }

  update() {
    const item = this.items[this.current];
    this.mainImage.src = item.full;
    this.mainImage.removeAttribute('srcset'); // clear srcset so browser uses src, not cached responsive set
    this.mainImage.alt = item.alt;
    this.syncModalImage(false);

    this.thumbs.forEach((thumb, index) => {
      const active = index === this.current;
      thumb.classList.toggle('is-active', active);
      thumb.setAttribute('aria-current', active ? 'true' : 'false');
      if (active) {
        this._scrollThumbIntoView(thumb);
      }
    });

    this.dots.forEach((dot, index) => {
      const active = index === this.current;
      dot.classList.toggle('is-active', active);
      dot.setAttribute('aria-current', active ? 'true' : 'false');
    });
  }

  /**
   * Keeps the active thumbnail visible inside its own horizontal strip only.
   * Scrolls the strip's scrollLeft directly instead of thumb.scrollIntoView(),
   * which can also drag the whole page when this runs from a remote trigger
   * (e.g. the sticky bar's variant switch) while the gallery is off-screen.
   */
  _scrollThumbIntoView(thumb) {
    const container = thumb.closest('.bc-product__thumbs');
    if (!container) return;

    const thumbLeft = thumb.offsetLeft;
    const thumbRight = thumbLeft + thumb.offsetWidth;
    const viewLeft = container.scrollLeft;
    const viewRight = viewLeft + container.clientWidth;

    if (thumbLeft < viewLeft) {
      container.scrollTo({ left: thumbLeft, behavior: 'smooth' });
    } else if (thumbRight > viewRight) {
      container.scrollTo({ left: thumbRight - container.clientWidth, behavior: 'smooth' });
    }
  }

  openModal() {
    if (!this.modal) return;
    this.syncModalImage(true);
    this.modal.classList.add('is-open');
    document.body.style.overflow = 'hidden';
    document.addEventListener('keydown', this._onKeyDown);
  }

  closeModal() {
    if (!this.modal) return;
    this.modal.classList.remove('is-open');
    document.body.style.overflow = '';
    document.removeEventListener('keydown', this._onKeyDown);
  }

  syncModalImage(forceLoad) {
    if (!this.modalImage) return;

    const item = this.items[this.current];
    this.modalImage.alt = item.alt;
    this.modalImage.dataset.initialSrc = item.full;

    if (forceLoad || this.modal?.classList.contains('is-open')) {
      this.modalImage.src = item.full;
      this.preloadNeighbors(this.current);
    } else {
      this.modalImage.removeAttribute('src');
    }
  }

  preloadNeighbors(index) {
    if (!this._preloadCache) this._preloadCache = {};
    const total = this.items.length;
    for (let offset = 1; offset <= 2; offset++) {
      const next = (index + offset) % total;
      const prev = (index - offset + total) % total;
      [next, prev].forEach((i) => {
        const src = this.items[i].full;
        if (!this._preloadCache[src]) {
          const img = new Image();
          img.src = src;
          this._preloadCache[src] = img;
        }
      });
    }
  }
}

class BCProductTabs {
  constructor(container) {
    this.container = container;
    this.buttons = Array.from(container.querySelectorAll('[role="tab"]'));
    this.panels = Array.from(container.querySelectorAll('[role="tabpanel"]'));
    if (this.buttons.length === 0) return;
    this.bind();
  }

  bind() {
    this.buttons.forEach((btn) => {
      btn.addEventListener('click', () => this.activate(btn));
    });

    this.container.addEventListener('keydown', (event) => {
      const index = this.buttons.indexOf(document.activeElement);
      if (index === -1) return;
      if (event.key === 'ArrowRight') {
        event.preventDefault();
        this.buttons[(index + 1) % this.buttons.length].focus();
      } else if (event.key === 'ArrowLeft') {
        event.preventDefault();
        this.buttons[(index - 1 + this.buttons.length) % this.buttons.length].focus();
      }
    });
  }

  activate(btn) {
    this.buttons.forEach((b) => {
      b.classList.toggle('is-active', b === btn);
      b.setAttribute('aria-selected', b === btn ? 'true' : 'false');
    });

    this.panels.forEach((panel) => {
      panel.hidden = panel.id !== btn.getAttribute('aria-controls');
    });
  }
}

/* ─────────────────────────────────────────────────────────────────────────────
   BCProductVariants
   Manages the bc-variant-picker snippet:
   – On page load: if ?variant= URL param → pre-select matching buttons, enable
     submit. Otherwise → disable submit, show "please select" message.
   – On pill click: resolve the matching variant, update hidden form input,
     update submit button and price display.
   Only active when [data-bc-variant-picker] is present (multi-variant products).
   ─────────────────────────────────────────────────────────────────────────── */
class BCProductVariants {
  constructor(section) {
    this.section = section;
    this.picker = section.querySelector('[data-bc-variant-picker]');
    if (!this.picker) return; // single-variant product — nothing to do

    const jsonEl = this.picker.querySelector('[data-bc-variants-json]');
    if (!jsonEl) return;

    this.variants = JSON.parse(jsonEl.textContent);

    // Merge per-variant inventory data from Liquid-embedded JSON.
    // Shopify excludes inventory_quantity from product.variants | json and /products/handle.js.
    const invEl = this.picker.querySelector('[data-bc-inventory-json]');
    if (invEl) {
      try {
        const invMap = JSON.parse(invEl.textContent);
        this.variants.forEach((v) => {
          const inv = invMap[String(v.id)];
          if (inv) {
            v.inventory_quantity  = inv.qty;
            v.inventory_management = inv.management;
            v.inventory_policy    = inv.policy;
          }
        });
      } catch (_) {}
    }

    this.hiddenInput = section.querySelector('[data-bc-variant-input]');
    this.submitBtn   = section.querySelector('[data-bc-submit]');
    this.msgEl       = section.querySelector('[data-bc-variant-msg]');
    this.skuEl       = section.querySelector('[data-bc-sku]');
    // Sticky bar mirrors the main submit state + variant selection
    this.stickySubmit = section.querySelector('[data-bc-sticky-submit]');
    this.stickyThumbImg = section.querySelector('[data-bc-sticky-thumb]');
    this.stickyDd = section.querySelector('[data-bc-variant-dd]');
    this.stickyTrigger = section.querySelector('[data-bc-variant-trigger]');
    this.stickyLabel = section.querySelector('[data-bc-variant-label]');
    this.stickyList = section.querySelector('[data-bc-variant-list]');
    this.stickyOptions = Array.from(section.querySelectorAll('[data-bc-variant-option]'));
    this.optionBtns = Array.from(this.picker.querySelectorAll('[data-bc-variant-btn]'));

    this._activeVariant = null;
    this._lastCartData = null;
    this._exhaustedVariantIds = new Set();

    // How many independent option groups (e.g. 2 = Size + Color)
    const optionEls = this.picker.querySelectorAll('[data-bc-option]');
    this.optionCount = optionEls.length;

    // Tracks current selection per option index (null = not yet chosen)
    this.selected = new Array(this.optionCount).fill(null);

    this._init();
    this._bind();
    this._prefetchState();
  }

  // ── Initialise state ──────────────────────────────────────────────────────

  _init() {
    const urlParams = new URLSearchParams(window.location.search);
    const urlVariantId = urlParams.get('variant');

    // Try URL param first
    if (urlVariantId) {
      const variant = this.variants.find((v) => String(v.id) === urlVariantId);
      if (variant) {
        variant.options.forEach((val, idx) => { this.selected[idx] = val; });
        this._syncButtons();
        this._applyVariant(variant);
        return;
      }
    }

    // Default: auto-select first available variant
    const defaultVariant = this.variants.find((v) => v.available) || this.variants[0];
    if (defaultVariant) {
      defaultVariant.options.forEach((val, idx) => { this.selected[idx] = val; });
      this._syncButtons();
      this._applyVariant(defaultVariant);
    }
  }

  // ── Event binding ─────────────────────────────────────────────────────────

  _bind() {
    this.optionBtns.forEach((btn) => {
      btn.addEventListener('click', () => this._onPillClick(btn));
    });

    // Sticky bar custom variant dropdown → drive the same variant change as the pills.
    // Selecting an option writes the hidden input, which triggers _onExternalVariantChange
    // (price/submit/pills + dropdown all sync).
    if (this.stickyDd && this.hiddenInput) {
      this.stickyTrigger?.addEventListener('click', (e) => {
        e.stopPropagation();
        this._toggleDd();
      });

      this.stickyOptions.forEach((opt) => {
        opt.addEventListener('click', () => {
          if (opt.classList.contains('is-disabled')) return;
          // Mirror the main pill path (_onPillClick → _applyVariant directly)
          // instead of writing hiddenInput.value via the setter, which opens a
          // re-entry window where Kaching Bundles can reset the selection.
          const variant = this.variants.find((v) => String(v.id) === opt.dataset.variantId);
          if (variant) {
            variant.options.forEach((val, idx) => { this.selected[idx] = val; });
            this._syncButtons();
            this._applyVariant(variant);
          }
          this._closeDd();
        });
      });

      // Close on outside click / Escape
      document.addEventListener('click', (e) => {
        if (!this.stickyDd.contains(e.target)) this._closeDd();
      });
      document.addEventListener('keydown', (e) => {
        if (e.key === 'Escape') this._closeDd();
      });
    }

    // ── Kaching Bundles sync ──────────────────────────────────────────────
    // When Kaching (or any external code) changes the hidden variant input,
    // we reflect that selection back into our BC pill UI.
    if (this.hiddenInput) {
      this._watchHiddenInput();
    }

    // Real-time stock guard: re-check availability after any cart change
    const onCartUpdated = (event) => {
      const cartData = event.detail ? event.detail.cartData : event.cartData;
      if (!cartData) return;
      this._lastCartData = cartData;
      this._exhaustedVariantIds.forEach((variantId) => {
        const cartItem = cartData.items.find((item) => item.variant_id === variantId);
        if (!cartItem) this._exhaustedVariantIds.delete(variantId);
      });
      this._checkStock(cartData);
    };
    document.addEventListener('bc:cart-updated', onCartUpdated);
    if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
      subscribe(PUB_SUB_EVENTS.cartUpdate, onCartUpdated);
    }

    // Cart item removed — clear local exhausted state
    document.addEventListener('click', (e) => {
      if (!e.target.closest('cart-remove-button')) return;
      this._exhaustedVariantIds.clear();
      this._lastCartData = null;
      const variant = this._activeVariant;
      if (!variant || !variant.available) return;
      this._setSubmitDisabled(false);
      const btnText = this.submitBtn?.querySelector('span');
      if (btnText) btnText.textContent = window.variantStrings?.addToCart || 'Add to cart';
      if (this.stickySubmit) this.stickySubmit.textContent = window.variantStrings?.addToCart || 'Add to cart';
    });

    document.addEventListener('bc:cart-exhausted', (event) => {
      const vid = event.detail.variantId;
      this._exhaustedVariantIds.add(vid);
      if (!this._activeVariant || vid !== this._activeVariant.id) return;
      this._setSubmitDisabled(true);
      const btnText = this.submitBtn?.querySelector('span');
      if (btnText) btnText.textContent = window.variantStrings?.soldOut || 'Sold out';
      if (this.stickySubmit) this.stickySubmit.textContent = window.variantStrings?.soldOut || 'Sold out';
    });
  }

  // On page load: fetch current cart + real inventory (JS API may return non-null for app-managed)
  _prefetchState() {
    const pathMatch = window.location.pathname.match(/\/products\/([^/?#]+)/);
    if (!pathMatch) return;
    const handle = pathMatch[1];

    Promise.all([
      fetch('/cart.js').then(r => r.json()),
      fetch('/products/' + handle + '.js').then(r => r.json()),
    ]).then(([cartData, productData]) => {
      this._lastCartData = cartData;

      // Patch variants where Liquid returned null inventory_quantity (Printify/POD)
      if (productData && Array.isArray(productData.variants)) {
        productData.variants.forEach((apiV) => {
          const v = this.variants.find((bv) => bv.id === apiV.id);
          if (v && v.inventory_quantity == null && apiV.inventory_quantity != null) {
            v.inventory_quantity = apiV.inventory_quantity;
            v.inventory_management = apiV.inventory_management;
            v.inventory_policy = apiV.inventory_policy;
          }
        });
      }

      this._checkStock(cartData);
    }).catch(() => {});
  }

  // Intercept external writes to the hidden input via value property override
  _watchHiddenInput() {
    const input = this.hiddenInput;
    let internalValue = input.value;
    const self = this;
    const nativeSetter = Object.getOwnPropertyDescriptor(HTMLInputElement.prototype, 'value').set;

    Object.defineProperty(input, 'value', {
      configurable: true,
      get() { return internalValue; },
      set(newVal) {
        const prev = internalValue;
        internalValue = newVal;
        nativeSetter.call(input, newVal);
        if (String(newVal) !== String(prev) && !self._bcUpdating) {
          self._onExternalVariantChange(String(newVal));
        }
      }
    });
  }

  _onExternalVariantChange(variantId) {
    if (!variantId) return;
    const variant = this.variants.find((v) => String(v.id) === variantId);
    if (!variant) return;

    // Sync BC pill selection state
    variant.options.forEach((val, idx) => { this.selected[idx] = val; });
    this._syncButtons();

    // Enable/disable submit and update price without re-writing the hidden input
    this._bcUpdating = true;
    this._applyVariant(variant);
    this._bcUpdating = false;
  }

  _onPillClick(btn) {
    const idx = parseInt(btn.dataset.optionIndex, 10);
    this.selected[idx] = btn.dataset.value;
    this._syncButtons();

    if (this._isFullySelected()) {
      const variant = this._findVariant();
      this._applyVariant(variant);
    } else {
      this._setSubmitDisabled(true);
      this._showMsg(true);
    }
  }

  // ── Sticky variant dropdown ─────────────────────────────────────────────────

  _toggleDd() {
    if (!this.stickyList) return;
    const isOpen = !this.stickyList.hidden;
    if (isOpen) this._closeDd();
    else this._openDd();
  }

  _openDd() {
    if (!this.stickyList) return;
    this.stickyList.hidden = false;
    this.stickyTrigger?.setAttribute('aria-expanded', 'true');
  }

  _closeDd() {
    if (!this.stickyList) return;
    this.stickyList.hidden = true;
    this.stickyTrigger?.setAttribute('aria-expanded', 'false');
  }

  _syncDropdown(variant) {
    if (!this.stickyDd) return;
    if (this.stickyLabel) this.stickyLabel.textContent = variant.title;
    this.stickyOptions.forEach((opt) => {
      const selected = String(opt.dataset.variantId) === String(variant.id);
      opt.classList.toggle('is-selected', selected);
      opt.setAttribute('aria-selected', selected ? 'true' : 'false');
    });
  }

  // ── Helpers ───────────────────────────────────────────────────────────────

  _isFullySelected() {
    return this.selected.every((v) => v !== null);
  }

  _findVariant() {
    return (
      this.variants.find((v) =>
        v.options.every((opt, i) => opt === this.selected[i])
      ) || null
    );
  }

  _syncButtons() {
    this.optionBtns.forEach((btn) => {
      const idx = parseInt(btn.dataset.optionIndex, 10);
      const active = this.selected[idx] === btn.dataset.value;
      btn.classList.toggle('is-active', active);
      btn.setAttribute('aria-pressed', active ? 'true' : 'false');
    });

    // Update selected-value labels ("Size: Medium")
    this.picker.querySelectorAll('[data-bc-option-val]').forEach((el) => {
      const idx = parseInt(el.dataset.bcOptionVal, 10);
      el.textContent = this.selected[idx] || '';
    });
  }

  _applyVariant(variant) {
    if (!variant) {
      // Combination exists in option space but no matching Shopify variant
      if (this.hiddenInput) this.hiddenInput.value = '';
      this._setSubmitDisabled(true);
      this._showMsg(true);
      return;
    }

    this._activeVariant = variant;

    // Update hidden form input
    if (this.hiddenInput) {
      this._bcUpdating = true;
      this.hiddenInput.value = variant.id;
      this.hiddenInput.disabled = !variant.available;
      this._bcUpdating = false;

      // Notify Kaching Bundles (and any other listeners) of the variant change
      this.hiddenInput.dispatchEvent(new Event('change', { bubbles: true }));
      this.hiddenInput.dispatchEvent(new Event('input',  { bubbles: true }));
    }

    // Update submit button text (main + sticky)
    const btnText = this.submitBtn?.querySelector('span');
    if (btnText) {
      btnText.textContent = variant.available ? (window.variantStrings?.addToCart || 'Add to cart') : (window.variantStrings?.soldOut || 'Sold out');
    }
    if (this.stickySubmit) {
      this.stickySubmit.textContent = variant.available ? (window.variantStrings?.addToCart || 'Add to cart') : (window.variantStrings?.soldOut || 'Sold out');
    }
    // Keep the sticky variant dropdown in sync with pill selection
    this._syncDropdown(variant);
    this._gallery?.goToMedia(variant.featured_media?.id);
    this._updateStickyThumb(variant);

    this._setSubmitDisabled(!variant.available);
    this._showMsg(false);
    this._updatePrice(variant);
    this._checkStock(this._lastCartData);
    // "X left in stock" text disabled for now — method left intact below,
    // just not called. Uncomment to re-enable.
    // this._updateStockCount(variant);
    this._updateSku(variant);
  }

  _checkStock(cartData) {
    const variant = this._activeVariant;
    if (!variant) return;
    if (variant.inventory_policy === 'continue') return;

    // For null-inventory products (Printify/POD), rely on the exhausted set
    if (this._exhaustedVariantIds.has(variant.id)) {
      this._setSubmitDisabled(true);
      const btnText = this.submitBtn?.querySelector('span');
      if (btnText) btnText.textContent = window.variantStrings?.soldOut || 'Sold out';
      if (this.stickySubmit) this.stickySubmit.textContent = window.variantStrings?.soldOut || 'Sold out';
      return;
    }

    if (!cartData || variant.inventory_quantity == null) return;

    const cartItem = cartData.items.find((item) => item.variant_id === variant.id);
    const cartQty = cartItem ? cartItem.quantity : 0;
    const remaining = variant.inventory_quantity - cartQty;
    const exhausted = remaining <= 0;
    const disabled = exhausted || !variant.available;

    this._setSubmitDisabled(disabled);
    const btnText = this.submitBtn?.querySelector('span');
    if (btnText) btnText.textContent = disabled ? (window.variantStrings?.soldOut || 'Sold out') : (window.variantStrings?.addToCart || 'Add to cart');
    if (this.stickySubmit) this.stickySubmit.textContent = disabled ? (window.variantStrings?.soldOut || 'Sold out') : (window.variantStrings?.addToCart || 'Add to cart');
  }

  _updateSku(variant) {
    if (!this.skuEl) return;
    const sku = variant?.sku;
    if (sku) {
      this.skuEl.textContent = 'SKU: ' + sku;
      this.skuEl.hidden = false;
    } else {
      this.skuEl.hidden = true;
    }
  }

  /** Sticky bar thumbnail — only swaps when the variant has its own image, same as the gallery. */
  _updateStickyThumb(variant) {
    if (!this.stickyThumbImg) return;
    const src = variant?.featured_media?.preview_image?.src;
    if (!src) return;

    const sep = src.includes('?') ? '&' : '?';
    this.stickyThumbImg.src = src + sep + 'width=112';
    this.stickyThumbImg.alt = variant.featured_media.alt || this.stickyThumbImg.alt;
  }

  _updateStockCount(variant) {
    const el = this.section.querySelector('[data-bc-stock-count]');
    if (!el) return;

    const qty     = variant.inventory_quantity;
    const managed = variant.inventory_management && variant.inventory_management !== '';
    const trackable = managed && variant.inventory_policy !== 'continue';

    // Only for genuinely low stock (≤5) — mirrors the Liquid threshold in
    // sections/bc-product-detail.liquid so switching variants client-side
    // doesn't disagree with the server-rendered initial state.
    if (trackable && qty > 0 && qty <= 5) {
      const template = window.variantStrings?.stockCount || '[count] left in stock';
      el.textContent = template.replace('[count]', qty);
      el.hidden = false;
    } else {
      el.hidden = true;
    }
  }

  _updatePrice(variant) {
    const priceEl = this.section.querySelector('.bc-product__price-current');
    const compareEl = this.section.querySelector('.bc-product__price-compare');

    if (priceEl) priceEl.textContent = this._formatMoney(variant.price);

    if (compareEl) {
      if (variant.compare_at_price > variant.price) {
        compareEl.textContent = this._formatMoney(variant.compare_at_price);
        compareEl.hidden = false;
      } else {
        compareEl.hidden = true;
      }
    }
  }

  _formatMoney(cents) {
    const currency = window.Shopify?.currency?.active || 'EUR';
    try {
      return new Intl.NumberFormat(document.documentElement.lang || 'en', {
        style: 'currency',
        currency,
        minimumFractionDigits: 2,
      }).format(cents / 100);
    } catch {
      return `${(cents / 100).toFixed(2)}`;
    }
  }

  _setSubmitDisabled(disabled) {
    if (this.submitBtn) this.submitBtn.disabled = disabled;
    if (this.stickySubmit) this.stickySubmit.disabled = disabled;
  }

  _showMsg(show) {
    if (!this.msgEl) return;
    if (show) {
      this.msgEl.removeAttribute('hidden');
    } else {
      this.msgEl.setAttribute('hidden', '');
    }
  }
}

/* ─────────────────────────────────────────────────────────────────────────────
   BCStockGuard
   Real-time stock exhaustion for single-variant products.
   Multi-variant products are handled inside BCProductVariants._checkStock().
   ─────────────────────────────────────────────────────────────────────────── */
class BCStockGuard {
  constructor(section) {
    if (section.querySelector('[data-bc-variant-picker]')) return;

    this.hiddenInput = section.querySelector('[data-bc-variant-input]');
    if (!this.hiddenInput) return;

    this.submitBtn = section.querySelector('[type="submit"][name="add"]');
    this.stickySubmit = section.querySelector('[data-bc-sticky-submit]');

    this._isExhausted = false;

    const onCartUpdated = (event) => {
      const cartData = event.detail ? event.detail.cartData : event.cartData;
      if (cartData) this._checkStock(cartData);
    };
    document.addEventListener('bc:cart-updated', onCartUpdated);
    if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
      subscribe(PUB_SUB_EVENTS.cartUpdate, onCartUpdated);
    }

    document.addEventListener('bc:cart-exhausted', (event) => {
      const variantId = parseInt(this.hiddenInput.value, 10);
      if (event.detail.variantId !== variantId) return;
      this._isExhausted = true;
      if (this.submitBtn) {
        this.submitBtn.disabled = true;
        const span = this.submitBtn.querySelector('span');
        if (span) span.textContent = window.variantStrings?.soldOut || 'Sold out';
      }
      if (this.stickySubmit) {
        this.stickySubmit.disabled = true;
        this.stickySubmit.textContent = window.variantStrings?.soldOut || 'Sold out';
      }
    });

    // Cart item removed — clear local exhausted state, no server request
    document.addEventListener('click', (e) => {
      if (!e.target.closest('cart-remove-button') || !this._isExhausted) return;
      this._isExhausted = false;
      if (this.submitBtn) {
        this.submitBtn.disabled = false;
        const span = this.submitBtn.querySelector('span');
        if (span) span.textContent = window.variantStrings?.addToCart || 'Add to cart';
      }
      if (this.stickySubmit) {
        this.stickySubmit.disabled = false;
        this.stickySubmit.textContent = window.variantStrings?.addToCart || 'Add to cart';
      }
    });

    // On page load: fetch cart + real inventory to set correct initial state
    const pathMatch = window.location.pathname.match(/\/products\/([^/?#]+)/);
    if (pathMatch) {
      const variantId = parseInt(this.hiddenInput.value, 10);
      const handle = pathMatch[1];
      Promise.all([
        fetch('/cart.js').then(r => r.json()),
        fetch('/products/' + handle + '.js').then(r => r.json()),
      ]).then(([cartData, productData]) => {
        // Patch inventory data attribute if Liquid returned null (app-managed inventory)
        if (productData && Array.isArray(productData.variants)) {
          const apiV = productData.variants.find(v => v.id === variantId);
          if (apiV && apiV.inventory_quantity != null) {
            this.hiddenInput.dataset.bcInventoryQty = apiV.inventory_quantity;
            this.hiddenInput.dataset.bcInventoryManagement = apiV.inventory_management || '';
            this.hiddenInput.dataset.bcInventoryPolicy = apiV.inventory_policy || 'deny';
          }
        }
        this._checkStock(cartData);
      }).catch(() => {});
    }
  }

  _checkStock(cartData) {
    const variantId = parseInt(this.hiddenInput.value, 10);
    const inventoryQty = parseInt(this.hiddenInput.dataset.bcInventoryQty, 10);
    const policy = this.hiddenInput.dataset.bcInventoryPolicy || 'deny';

    if (!variantId || isNaN(inventoryQty) || policy === 'continue') return;

    const cartItem = cartData.items.find((item) => item.variant_id === variantId);
    const cartQty = cartItem ? cartItem.quantity : 0;
    const exhausted = cartQty >= inventoryQty;

    if (this.submitBtn) {
      this.submitBtn.disabled = exhausted;
      const span = this.submitBtn.querySelector('span');
      if (span) span.textContent = exhausted ? (window.variantStrings?.soldOut || 'Sold out') : (window.variantStrings?.addToCart || 'Add to cart');
    }
    if (this.stickySubmit) {
      this.stickySubmit.disabled = exhausted;
      this.stickySubmit.textContent = exhausted ? (window.variantStrings?.soldOut || 'Sold out') : (window.variantStrings?.addToCart || 'Add to cart');
    }

    // "X left in stock" text disabled for now — method left intact below,
    // just not called. Uncomment to re-enable.
    // this._updateStockCount(inventoryQty);
  }

  _updateStockCount(inventoryQty) {
    const el = document.querySelector('[data-bc-stock-count]');
    if (!el) return;

    const management = this.hiddenInput.dataset.bcInventoryManagement || '';
    const policy     = this.hiddenInput.dataset.bcInventoryPolicy     || 'deny';

    // Only for genuinely low stock (≤5) — mirrors the Liquid threshold in
    // sections/bc-product-detail.liquid so switching variants client-side
    // doesn't disagree with the server-rendered initial state.
    if (management && policy !== 'continue' && inventoryQty > 0 && inventoryQty <= 5) {
      const template = window.variantStrings?.stockCount || '[count] left in stock';
      el.textContent = template.replace('[count]', inventoryQty);
      el.hidden = false;
    } else {
      el.hidden = true;
    }
  }
}

/* ─────────────────────────────────────────────────────────────────────────────
   BCStickyAtc
   Shows the bottom sticky add-to-cart bar once the main .product-form__buttons
   block has scrolled up out of view. Hides it again when that block is visible
   (or hasn't been reached yet on initial load).
   ─────────────────────────────────────────────────────────────────────────── */
class BCStickyAtc {
  constructor(section) {
    this.bar = section.querySelector('[data-bc-sticky-atc]');
    this.anchor = section.querySelector('.product-form__buttons');
    if (!this.bar || !this.anchor) return;

    if ('IntersectionObserver' in window) this._observe();
  }

  _observe() {
    this.io = new IntersectionObserver((entries) => {
      const entry = entries[0];
      if (!entry) return;
      // Show only when the main button has been scrolled PAST (above the viewport),
      // not when it's still below the fold before the user reaches it.
      const scrolledPast = !entry.isIntersecting && entry.boundingClientRect.top < 0;
      this.bar.classList.toggle('is-visible', scrolledPast);
      this.bar.setAttribute('aria-hidden', scrolledPast ? 'false' : 'true');
    }, { threshold: 0 });
    this.io.observe(this.anchor);
  }
}


/* ─────────────────────────────────────────────────────────────────────────────
   Drag-to-scroll for any horizontally scrollable row
   Pass the element; it gets mouse-drag scrolling on desktop, touch is native.
   ─────────────────────────────────────────────────────────────────────────── */
function initDragScroll(el) {
  if (!el) return;
  let isDown = false;
  let startX = 0;
  let scrollLeft = 0;

  el.addEventListener('mousedown', (e) => {
    isDown = true;
    el.classList.add('is-dragging');
    startX = e.pageX - el.offsetLeft;
    scrollLeft = el.scrollLeft;
  });

  el.addEventListener('mouseleave', () => {
    isDown = false;
    el.classList.remove('is-dragging');
  });

  el.addEventListener('mouseup', () => {
    isDown = false;
    el.classList.remove('is-dragging');
  });

  el.addEventListener('mousemove', (e) => {
    if (!isDown) return;
    e.preventDefault();
    const x = e.pageX - el.offsetLeft;
    const walk = (x - startX) * 1.2;
    el.scrollLeft = scrollLeft - walk;
  });
}

function initBCProductDetail() {
  const cartCountTpl = window.variantStrings?.cartCount || 'In more than [count] baskets';

  // sessionStorage key per product URL — stable until tab closes, resets on new session
  const storageKey = 'bc_cart_count_' + window.location.pathname;
  let savedCount = NaN;
  try { savedCount = parseInt(sessionStorage.getItem(storageKey), 10); } catch (_) {}

  document.querySelectorAll('[data-bc-product-detail]').forEach((section) => {
    if (section.dataset.bcProductInit === 'true') return;
    section.dataset.bcProductInit = 'true';

    // Social-proof cart count: random 1–min(12, stockQty), stable for the tab session
    const cartCountEl = section.querySelector('[data-bc-cart-count]');
    if (cartCountEl) {
      const stockQty = parseInt(
        section.querySelector('[data-bc-variant-input]')?.dataset.bcInventoryQty, 10
      );

      // Hide when explicitly out of stock; show when tracked (qty > 0) or untracked (NaN)
      if (stockQty !== 0) {
        const maxCount = (stockQty > 0) ? Math.min(12, stockQty) : 12;

        let count = savedCount;
        if (!(count >= 1 && count <= maxCount)) {
          count = Math.floor(Math.random() * maxCount) + 1; // 1–maxCount inclusive
          try { sessionStorage.setItem(storageKey, count); } catch (_) {}
        }

        // Badge only reads as credible social proof from 3 baskets up —
        // "In more than 1 basket" undersells rather than reassures. The
        // random draw itself is untouched (still 1–maxCount, stable per
        // session via sessionStorage above); a 1 or 2 draw just means the
        // badge stays hidden for that session instead of showing a
        // weak number.
        if (count >= 3) {
          cartCountEl.textContent = cartCountTpl.replace('[count]', count);
          cartCountEl.hidden = false;
        } else {
          cartCountEl.hidden = true;
        }
      }
    }

    const gallery = new BCProductGallery(section);
    const variantPicker = new BCProductVariants(section);
    variantPicker._gallery = gallery;
    new BCStockGuard(section);
    new BCStickyAtc(section);
    const tabsEl = section.querySelector('[data-bc-tabs]');
    if (tabsEl) new BCProductTabs(tabsEl);
    initDragScroll(section.querySelector('.bc-product__tab-list'));
  });
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initBCProductDetail);
} else {
  initBCProductDetail();
}

document.addEventListener('shopify:section:load', (event) => {
  const section = event.target.querySelector('[data-bc-product-detail]');
  if (!section) return;
  section.dataset.bcProductInit = 'false';
  initBCProductDetail();
});

/* ── YouTube video modal ── */
(function () {
  const modal  = document.getElementById('bc-yt-modal');
  const iframe = document.getElementById('bc-yt-iframe');
  if (!modal || !iframe) return;

  function extractYtId(src) {
    try {
      const url = new URL(src);
      if (url.hostname.includes('youtu.be')) return url.pathname.slice(1).split('?')[0];
      if (url.hostname.includes('youtube.com')) {
        if (url.pathname.includes('/embed/')) return url.pathname.split('/embed/')[1].split('?')[0];
        return url.searchParams.get('v');
      }
    } catch (e) {}
    return null;
  }

  function openModal(ytId) {
    iframe.src = 'https://www.youtube.com/embed/' + ytId + '?autoplay=1&rel=0';
    modal.hidden = false;
    document.body.style.overflow = 'hidden';
  }

  function closeModal() {
    modal.hidden = true;
    iframe.src = '';
    document.body.style.overflow = '';
  }

  /* Convert inline YouTube iframes inside tab panels into clickable thumbnails */
  function convertIframes() {
    document.querySelectorAll('.bc-product__tab-panel iframe').forEach(function (el) {
      const src = el.getAttribute('src') || '';
      if (!src.includes('youtube') && !src.includes('youtu.be')) return;
      const ytId = extractYtId(src);
      if (!ytId) return;

      const wrap = document.createElement('div');
      wrap.className = 'bc-yt-thumb';
      wrap.dataset.bcYtModal = '';
      wrap.dataset.ytId = ytId;
      wrap.innerHTML =
        '<img class="bc-yt-thumb__img" loading="lazy" alt="Video"' +
        ' src="https://img.youtube.com/vi/' + ytId + '/maxresdefault.jpg"' +
        ' onerror="this.src=\'https://img.youtube.com/vi/' + ytId + '/hqdefault.jpg\'">' +
        '<button class="bc-yt-thumb__play" type="button" aria-label="Play video">' +
        '<svg width="64" height="64" viewBox="0 0 64 64" fill="none">' +
        '<circle cx="32" cy="32" r="32" fill="rgba(0,0,0,0.55)"/>' +
        '<path d="M26 20L46 32L26 44V20Z" fill="white"/></svg></button>';

      el.parentNode.replaceChild(wrap, el);
    });
  }

  convertIframes();

  // Click + touchend — закриває по backdrop і кнопці X
  function handleClose(e) {
    const thumb = e.target.closest('[data-bc-yt-modal]');
    if (thumb) { openModal(thumb.dataset.ytId); return; }
    if (e.target.closest('[data-bc-yt-close]')) { closeModal(); return; }
    // Клік по backdrop (не по dialog)
    if (!modal.hidden && e.target === modal) { closeModal(); return; }
  }

  document.addEventListener('click', handleClose);

  // На мобілці backdrop може не отримати click через iframe — дублюємо через touchend
  modal.addEventListener('touchend', function (e) {
    if (e.target === modal || e.target.closest('[data-bc-yt-close]')) {
      e.preventDefault();
      closeModal();
    }
  });

  document.addEventListener('keydown', function (e) {
    if (e.key === 'Escape' && !modal.hidden) closeModal();
  });
})();
